import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import {can, canOpenRoute, canSeeEconomics} from "../lib/permissions";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { fmtEUR, fmtNum, fmtMonthShort, VEHICLE_STATUS } from "../lib/format";
import { AlertBadge } from "../components/StatusBadge";
import { CHART_TOOLTIP_STYLE } from "../lib/chartTheme";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, LineChart, Line, Label,
} from "recharts";
import {
  Car, CheckCircle2, KeyRound, PackageCheck, CalendarCheck2, ChartColumn, ChartPie, Calculator, Wrench, XCircle, CalendarClock, Gauge, Euro,
  TrendingUp, Activity, AlertTriangle, Contact, ParkingCircle, LayoutDashboard, ArrowRight,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import DriverToday from "../components/DriverToday";
import TodayTasks, { TodayActions } from "../components/TodayTasks";
import ActivityList from "../components/ActivityList";

const PIE_COLORS = ["#10b981", "#38bdf8", "#6366f1", "#f59e0b", "#ef4444", "#f97316", "#94a3b8"];

const KPI_TONES = { primary: "", green: "tone-green", indigo: "tone-indigo", amber: "tone-amber", red: "tone-red" };

function KpiCard({ icon: Icon, label, value, sub, testid, tone = "primary" }) {
  return (
    <Card className="min-w-0 border-border bg-card p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center gap-3 text-muted-foreground">
        <span className={`icon-tile h-9 w-9 ${KPI_TONES[tone] || ""}`} aria-hidden="true"><Icon className="h-5 w-5" strokeWidth={2} /></span>
        <p className="text-sm leading-tight">{label}</p>
      </div>
      <p className="mt-2 break-words font-heading text-2xl font-bold tracking-tight tabular-nums sm:text-3xl">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [clients, setClients] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canSeeClients = can(user,"customers.read");
  const economics = canSeeEconomics(user);
  // Drivers who collect handovers but do not run the office open on their day.
  const fieldDriver = can(user,"rentals.handover_collect") && !can(user,"rentals.handover");
  const operational = can(user,"statistics.operational") && (user.active_site_id !== "all" || can(user,"statistics.multisite"));
  const managementNavigation = (to) => canOpenRoute(user,to) ? {
    onClick: () => navigate(to), role: "button", tabIndex: 0,
    onKeyDown: (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); navigate(to); } },
  } : {};
  // Looks clickable only when it really opens its destination.
  const managementCardClass = (to) => `border-border bg-card p-4 ${canOpenRoute(user,to) ? "cursor-pointer transition-colors hover:border-primary/40" : ""}`;

  useEffect(() => {
    let active = true;
    setStats(null); setError("");
    (can(user,"statistics.operational")||can(user,"economic.summary")?api.get("/dashboard/stats"):Promise.resolve({data:{}})).then((r) => { if (active) setStats(r.data); }).catch((e) => { if (active) setError(apiError(e)); });
    api.get("/notifications").then((r) => { if (active) setAlerts(r.data); }).catch(() => {});
    if (canSeeClients && economics) api.get("/clients/summary").then((r) => { if (active) setClients(r.data); }).catch(() => { if (active) setClients({ error: true }); });
    return () => { active = false; };
  // Access changes remount the protected page.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeClients, economics, retry]);

  if (error) return <Card className="border-border bg-card p-5" role="alert"><p>{error}</p><Button className="mt-3" variant="outline" onClick={() => setRetry((n) => n + 1)}>Riprova</Button></Card>;
  if (!stats) return (
    <div role="status" className="space-y-6">
      <span className="sr-only">Caricamento dashboard…</span>
      <div className="skeleton h-9 w-56" aria-hidden="true" />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" aria-hidden="true">
        {Array.from({ length: 8 }, (_, i) => <div key={i} className="skeleton h-28 rounded-xl" />)}
      </div>
      <div className="skeleton h-64 rounded-xl" aria-hidden="true" />
    </div>
  );

  const lead = stats.alert_lead || {};
  const statusData = Object.entries(stats.by_status || {}).map(([k, v]) => ({ name: k === "assegnato" ? "In uso" : k === "prenotato" ? "Prenotati oggi" : VEHICLE_STATUS[k]?.label || k, value: v }));
  const costData = (stats.monthly_costs || []).map((m) => ({
    name: fmtMonthShort(m.month), total: m.total,
  }));

  return (
    <div className="space-y-6 animate-fade-up">
      {fieldDriver ? <DriverToday /> : <PageHeader icon={LayoutDashboard} title="Dashboard" description="Panoramica della flotta AutoTu" />}
      {fieldDriver && operational && <h2 className="pt-2 font-heading text-lg font-semibold">Panoramica della flotta</h2>}

      {!fieldDriver && (operational ? <TodayTasks stats={stats} user={user} lead={stats.alert_lead} actions={<TodayActions user={user} handover={can(user,"rentals.handover")} />} /> : <TodayActions user={user} handover={can(user,"rentals.handover")} />)}
      {!!stats.warnings?.length && <Card className="border-amber-500/40 bg-amber-500/10 p-4 text-sm" role="status"><p className="font-medium">I dati presentano alcune limitazioni:</p><ul className="mt-2 list-disc space-y-1 pl-5">{stats.warnings.map((warning, i) => <li key={i}>{warning}</li>)}</ul></Card>}

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {operational && <><KpiCard icon={Car} label="Veicoli totali" value={stats.total_vehicles} tone="primary" testid="kpi-total" />
        <KpiCard icon={CheckCircle2} label="Disponibili" value={stats.available} tone="green" testid="kpi-available" />
        <KpiCard icon={KeyRound} label="In uso" value={stats.in_use} tone="indigo" testid="kpi-assigned" />
        <KpiCard icon={CalendarCheck2} label="Prenotati oggi" value={stats.reserved} sub="Esclusi i mezzi già in uso o in consegna" tone="primary" testid="kpi-reserved" />
        <KpiCard icon={PackageCheck} label="Consegna da confermare" value={stats.in_delivery} tone="indigo" testid="kpi-delivery" />
        <KpiCard icon={Wrench} label="In manutenzione" value={stats.maintenance} tone="amber" testid="kpi-maintenance" />
        <KpiCard icon={XCircle} label="Non operativi" value={stats.not_operational} tone="red" testid="kpi-notop" />
        <KpiCard icon={Gauge} label="Km flotta" value={fmtNum(stats.total_km)} tone="primary" testid="kpi-km" /></>}
        {economics && <><KpiCard icon={Euro} label="Costo mese" value={fmtEUR(stats.month_cost)} tone="green" testid="kpi-month-cost" />
        <KpiCard icon={Calculator} label="Costo medio/veicolo" value={fmtEUR(stats.avg_cost_per_vehicle)} tone="indigo" testid="kpi-avg-cost" />
        <KpiCard icon={TrendingUp} label="Costi/km contachilometri" value={fmtEUR(stats.cost_per_km)} sub="Sui km attuali della flotta" tone="green" testid="kpi-cost-km" /></>}
      </div>

      {/* Cost per km trend */}
      {economics && <Card className="chart-primary border-border bg-card p-5" data-testid="cost-km-trend">
        <div className="mb-2 flex items-center gap-2"><span className="icon-tile h-8 w-8 rounded-lg" aria-hidden="true"><TrendingUp strokeWidth={2} className="h-4 w-4" /></span><h3 className="font-heading text-lg font-semibold">Costi mensili / km contachilometri</h3></div>
        <p className="mb-4 text-xs text-muted-foreground">Ogni mese usa il contachilometri attuale della flotta come denominatore, non i km percorsi nel mese.</p>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={(stats.monthly_costs || []).map((m) => ({ name: fmtMonthShort(m.month), cost_km: m.cost_km }))}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `€${v}`} />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => fmtEUR(v) + "/km"} />
            <Line type="monotone" dataKey="cost_km" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      }
      {/* Canoni mensili attivi + Giorni di fermo */}
      <div className={canSeeClients ? "grid grid-cols-1 gap-4 lg:grid-cols-2" : ""}>
        {canSeeClients && economics && (
        <Card className="border-border bg-card p-5" data-testid="clients-revenue-card">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2"><span className="icon-tile h-8 w-8 rounded-lg" aria-hidden="true"><Contact strokeWidth={2} className="h-4 w-4" /></span><h3 className="font-heading text-lg font-semibold">Canoni mensili attivi</h3></div>
            <Button asChild variant="ghost" size="sm" className="min-h-9 text-primary"><Link to="/clienti" data-testid="clients-see-all">Vedi tutti<ArrowRight aria-hidden="true" /></Link></Button>
          </div>
          {clients && clients.error ? (
            <p className="py-12 text-center text-sm text-muted-foreground">Impossibile caricare i dati clienti</p>
          ) : clients ? (
            <>
              <div className="mb-4 flex flex-wrap items-end gap-x-8 gap-y-3">
                <div><p className="text-xs text-muted-foreground">Canoni mensili attivi</p><p className="font-heading text-3xl font-bold text-emerald-400" data-testid="clients-total-revenue">{fmtEUR(clients.total_monthly_revenue)}</p></div>
                <div><p className="text-xs text-muted-foreground">Clienti attivi</p><p className="font-heading text-2xl font-bold">{clients.active_client_count}</p></div>
                <div><p className="text-xs text-muted-foreground">Veicoli a noleggio</p><p className="font-heading text-2xl font-bold">{clients.assigned_vehicle_count}</p></div>
              </div>
              <div className="space-y-2">
                {clients.clients.filter((c) => c.vehicle_count > 0).slice(0, 5).map((c) => (
                  <div key={c.id} className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-3 py-2.5" data-testid={`client-row-${c.id}`}>
                    <div className="min-w-0"><p className="truncate text-sm font-medium">{c.name}</p><p className="text-xs text-muted-foreground">{c.vehicle_count} {c.vehicle_count === 1 ? "veicolo" : "veicoli"}</p></div>
                    <span className="shrink-0 text-sm font-semibold text-emerald-400">{fmtEUR(c.monthly_revenue)}/mese</span>
                  </div>
                ))}
                {clients.active_client_count === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Nessun cliente con veicoli attivi</p>}
              </div>
            </>
          ) : <div role="status" className="space-y-2"><span className="sr-only">Caricamento clienti…</span>{[0, 1, 2].map((i) => <div key={i} className="skeleton h-12" aria-hidden="true" />)}</div>}
        </Card>
        )}

        {operational && <Card className="border-border bg-card p-5" data-testid="idle-chart-card">
          <div className="mb-4 flex items-center gap-2"><span className="icon-tile h-8 w-8 rounded-lg tone-amber" aria-hidden="true"><ParkingCircle strokeWidth={2} className="h-4 w-4" /></span><h3 className="font-heading text-lg font-semibold">Giorni di fermo per veicolo</h3></div>
          {stats.idle_days_by_vehicle && stats.idle_days_by_vehicle.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.idle_days_by_vehicle} layout="vertical" margin={{ left: 6, right: 12 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="plate" width={82} stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(val) => [`${val} giorni`, "Fermo"]} />
                <Bar dataKey="days" radius={[0, 4, 4, 0]}>
                  {stats.idle_days_by_vehicle.map((e, i) => <Cell key={i} fill={e.days >= 90 ? "#ef4444" : e.days >= 30 ? "#f59e0b" : "#38bdf8"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="py-16 text-center text-sm text-muted-foreground">Nessun veicolo fermo</p>}
        </Card>}
      </div>

      {/* Deadlines + maintenance strip */}
      {operational && <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className={managementCardClass("/scadenze")} {...managementNavigation("/scadenze")} data-testid="kpi-exp7">
          <div className="flex items-center gap-3"><span className="icon-tile h-8 w-8 rounded-lg tone-red" aria-hidden="true"><CalendarClock strokeWidth={2} className="h-4 w-4" /></span><div><p className="text-xs text-muted-foreground">Scadenze ≤ {lead.days7} giorni</p><p className="font-heading text-xl font-bold">{stats.expiring_7}</p></div></div>
        </Card>
        <Card className={managementCardClass("/scadenze")} {...managementNavigation("/scadenze")} data-testid="kpi-exp15">
          <div className="flex items-center gap-3"><span className="icon-tile h-8 w-8 rounded-lg tone-amber" aria-hidden="true"><CalendarClock strokeWidth={2} className="h-4 w-4" /></span><div><p className="text-xs text-muted-foreground">Scadenze ≤ {lead.days15} giorni</p><p className="font-heading text-xl font-bold">{stats.expiring_15}</p></div></div>
        </Card>
        <Card className={managementCardClass("/scadenze")} {...managementNavigation("/scadenze")} data-testid="kpi-exp30">
          <div className="flex items-center gap-3"><span className="icon-tile h-8 w-8 rounded-lg tone-sky" aria-hidden="true"><CalendarClock strokeWidth={2} className="h-4 w-4" /></span><div><p className="text-xs text-muted-foreground">Scadenze ≤ {lead.days30} giorni</p><p className="font-heading text-xl font-bold">{stats.expiring_30}</p></div></div>
        </Card>
        <Card className={managementCardClass("/manutenzioni")} {...managementNavigation("/manutenzioni")} data-testid="kpi-maint-due">
          <div className="flex items-center gap-3"><span className="icon-tile h-8 w-8 rounded-lg" aria-hidden="true"><Wrench strokeWidth={2} className="h-4 w-4" /></span><div><p className="text-xs text-muted-foreground">Manutenzioni da fare</p><p className="font-heading text-xl font-bold">{stats.maintenances_due}</p></div></div>
        </Card>
      </div>

      }
      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {economics && <Card className="chart-primary border-border bg-card p-5 lg:col-span-2">
          <div className="mb-4 flex items-center gap-2"><span className="icon-tile h-8 w-8 rounded-lg" aria-hidden="true"><ChartColumn strokeWidth={2} className="h-4 w-4" /></span><h3 className="font-heading text-lg font-semibold">Costi mensili</h3></div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={costData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `€${v / 1000}k`} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => fmtEUR(v)} />
              <Bar dataKey="total" fill="#0ea5e9" radius={[6, 6, 0, 0]} maxBarSize={44} />
            </BarChart>
          </ResponsiveContainer>
        </Card>}

        {operational && <Card className="border-border bg-card p-5">
          <div className="mb-4 flex items-center gap-2"><span className="icon-tile h-8 w-8 rounded-lg" aria-hidden="true"><ChartPie strokeWidth={2} className="h-4 w-4" /></span><h3 className="font-heading text-lg font-semibold">Distribuzione per stato</h3></div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={62} outerRadius={88} paddingAngle={2} stroke="none" cornerRadius={4}>
                {statusData.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                <Label content={({ viewBox }) => <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle"><tspan x={viewBox.cx} dy="-0.3em" className="chart-center-value">{statusData.reduce((sum, item) => sum + item.value, 0)}</tspan><tspan x={viewBox.cx} dy="1.6em" className="chart-center-label">veicoli</tspan></text>} />
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 flex flex-wrap gap-2">
            {statusData.map((s, i) => (
              <span key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{s.name} ({s.value})
              </span>
            ))}
          </div>
        </Card>}
      </div>

      {/* Alerts + activity */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="border-border bg-card p-5" data-testid="alerts-panel">
          <div className="mb-4 flex items-center gap-2"><span className="icon-tile h-8 w-8 rounded-lg tone-amber" aria-hidden="true"><AlertTriangle strokeWidth={2} className="h-4 w-4" /></span><h3 className="font-heading text-lg font-semibold">Avvisi urgenti</h3></div>
          <div className="space-y-2">
            {alerts.slice(0, 6).map((a) => (
              <div key={a.id + a.type} className="flex items-center justify-between rounded-lg border border-border bg-secondary/30 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{a.title}</p>
                  <p className="truncate text-xs text-muted-foreground">{a.message}</p>
                </div>
                <AlertBadge type={a.type} severity={a.severity} />
              </div>
            ))}
            {alerts.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Nessun avviso urgente</p>}
          </div>
        </Card>

        <Card className="border-border bg-card p-5">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2"><span className="icon-tile h-8 w-8 rounded-lg" aria-hidden="true"><Activity strokeWidth={2} className="h-4 w-4" /></span><h3 className="font-heading text-lg font-semibold">Attività recenti</h3></div>
            {canOpenRoute(user, "/attivita") && <Link to="/attivita" className="inline-flex min-h-9 items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid="activity-see-all">Vedi tutte<ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>}
          </div>
          <ActivityList rows={stats.recent_activity || []} compact />
          {(stats.recent_activity || []).length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Nessuna attività</p>}
        </Card>
      </div>
    </div>
  );
}
